/**
 * Concessions List Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useConcessions hook.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Loader2, Plus } from 'lucide-react';
import {
  useConcessions,
  StatsCards,
  BreakdownCards,
  ConcessionsTable,
  CreateConcessionDialog,
} from './concessions-list';
import { platformSettingsAPI, QualityTabsVisibility } from '../lib/api';

export default function Concessions() {
  const navigate = useNavigate();
  const {
    // User info
    isSupplier,

    // Loading states
    loading,
    error,
    createLoading,
    createError,

    // Data
    stats,
    concessions,
    searchTerm,
    filters,
    sortField,
    sortDirection,
    pagination,

    // Create dialog
    showCreateDialog,
    setShowCreateDialog,
    newConcession,
    setNewConcession,

    // Actions
    fetchData,
    handleSort,
    handleViewConcession,
    handleCreateConcession,
    handleSearchChange,
    handleFiltersChange,
  } = useConcessions();

  // Quality tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // CR-6385: the page needs the concession feature ON as well as the tab shown.
  // Direct-URL access is the exact hole the legacy platform left open, so the
  // route guards on the switch and not only on the admin layout choice.
  //
  // Read from THIS page's own settings fetch rather than MenuSettingsContext.
  // The context loads independently and starts at false, so gating on the two
  // together raced: when this fetch resolved first, a tenant that genuinely has
  // concessions on was redirected away from a page it is entitled to. One
  // source, one loaded flag, no ordering to get wrong.
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);
  const concessionsAvailable = tabVisibility.concessions && concessionsEnabled;

  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
        setConcessionsEnabled(!!res.data?.concessions_enabled);
      })
      // Fail closed: if we cannot establish that the feature is on, treat it
      // as off. The server refuses the writes either way.
      .catch(() => setConcessionsEnabled(false))
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if concessions tab is disabled - navigate to first available quality tab
  useEffect(() => {
    if (!tabSettingsLoaded) return;

    if (!concessionsAvailable) {
      // Concessions disabled - redirect to first available tab
      if (tabVisibility.ncrs) {
        navigate('/quality', { replace: true });
      } else if (tabVisibility.approval_register && !isSupplier) {
        navigate('/intelligence/qualifications', { replace: true });
      }
      // If no tabs are available, stay on page but content won't show
    }
  }, [tabSettingsLoaded, tabVisibility, concessionsAvailable, navigate, isSupplier]);

  // Show loading while checking tab settings
  if (!tabSettingsLoaded || (loading && !concessions.length)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If concessions tab is disabled, show access denied (redirect should have happened)
  if (!concessionsAvailable) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* CR-3873: Main Card Container with consistent Quality navigation tabs */}
      <Card className="rounded-lg shadow-sm">
        {/* Navigation Pills Header - matches Quality page */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-2">
              {tabVisibility.ncrs && (
                <Link
                  to="/quality"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  NCRs
                </Link>
              )}
              {tabVisibility.concessions && (
                <Link
                  to="/concessions"
                  className="px-4 py-2 text-sm font-medium rounded-md border-2 border-primary bg-primary text-primary-foreground shadow-sm"
                >
                  Production Permit / Concession
                </Link>
              )}
              {tabVisibility.approval_register && (
                <Link
                  to="/quality/approval-register"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  Approval Register
                </Link>
              )}
            </nav>
            {isSupplier && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Concession
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* KPI Cards */}
          <StatsCards stats={stats} />

          {/* Risk Level & Deviation Type Breakdown */}
          <BreakdownCards stats={stats} />

          {/* Concessions Table */}
          <ConcessionsTable
            concessions={concessions}
            searchTerm={searchTerm}
            filters={filters}
            sortField={sortField}
            sortDirection={sortDirection}
            isSupplier={isSupplier}
            pagination={pagination}
            onSearchChange={handleSearchChange}
            onFiltersChange={handleFiltersChange}
            onSort={handleSort}
            onViewConcession={handleViewConcession}
          />
        </CardContent>
      </Card>

      {/* Create Concession Dialog. `createError` is deliberately NOT the
          page-level `error` above — a failed create must not replace the page
          and take the half-typed form with it. */}
      <CreateConcessionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        form={newConcession}
        onFormChange={setNewConcession}
        onSubmit={handleCreateConcession}
        loading={createLoading}
        error={createError}
      />
    </div>
  );
}
